import { FILTER_SPLIT_KEY } from "@/constants";
import type { BlokShoeFinderStep } from "@/storyblok";

/**
 * Parse a comma-separated tag string into a trimmed list. Accepts unknown so
 * it works against the Storyblok generated types where custom fields land on
 * the `[k: string]: unknown` index signature.
 */
function parseTags(value: unknown): string[] {
	if (typeof value !== "string") return [];
	return value
		.split(",")
		.map((t) => t.trim())
		.filter(Boolean);
}

/**
 * Project the longest reachable path the user could traverse from where they
 * are now. Steps before `currentStepIndex` use the user's locked-in selections;
 * for the current step and beyond we try each answer individually and pick the
 * single-branch choice that yields the longest run, so the denominator reflects
 * a realistic max (not the union of all mutually-exclusive branches).
 */
export function computeMaxVisibleSteps(
	steps: BlokShoeFinderStep[],
	selectionsByUid: Map<string, Set<string>>,
	currentStepIndex: number,
): number {
	function walk(arrayIdx: number, unlocked: Set<string>, visibleIdx: number): number {
		if (arrayIdx >= steps.length) return 0;
		const step = steps[arrayIdx];
		const required = parseTags(step.showIfPath);
		const show = required.length === 0 || required.some((t) => unlocked.has(t));
		if (!show) return walk(arrayIdx + 1, unlocked, visibleIdx);

		if (!step.answers?.length) {
			return 1 + walk(arrayIdx + 1, unlocked, visibleIdx + 1);
		}

		if (visibleIdx < currentStepIndex) {
			const selected = selectionsByUid.get(step._uid);
			const next = new Set(unlocked);
			if (selected?.size) {
				for (const answer of step.answers) {
					if (!selected.has(answer._uid)) continue;
					for (const tag of parseTags(answer.pathTag)) next.add(tag);
				}
			}
			return 1 + walk(arrayIdx + 1, next, visibleIdx + 1);
		}

		let maxRest = 0;
		for (const answer of step.answers) {
			const next = new Set(unlocked);
			for (const tag of parseTags(answer.pathTag)) next.add(tag);
			const rest = walk(arrayIdx + 1, next, visibleIdx + 1);
			if (rest > maxRest) maxRest = rest;
		}
		return 1 + maxRest;
	}

	return walk(0, new Set<string>(), 0);
}

/**
 * Walk `steps` in order and return only the ones that are reachable given the
 * user's current selections. A step is reachable when it has no `showIfPath`
 * or when at least one of its `showIfPath` tags was unlocked by an earlier
 * selected answer's `pathTag`.
 *
 * Selections on steps that are not reachable are ignored (preserving the
 * selection map across branch switches without leaking tags).
 */
export function computeVisibleSteps(
	steps: BlokShoeFinderStep[],
	selectionsByUid: Map<string, Set<string>>,
): BlokShoeFinderStep[] {
	const visible: BlokShoeFinderStep[] = [];
	const unlocked = new Set<string>();

	for (const step of steps) {
		const required = parseTags(step.showIfPath);
		const show = required.length === 0 || required.some((t) => unlocked.has(t));
		if (!show) continue;

		visible.push(step);

		const selected = selectionsByUid.get(step._uid);
		if (!selected?.size || !step.answers) continue;
		for (const answer of step.answers) {
			if (!selected.has(answer._uid)) continue;
			for (const tag of parseTags(answer.pathTag)) {
				unlocked.add(tag);
			}
		}
	}

	return visible;
}

export type ShoeFinderFilterPayload = {
	/**
	 * One group per selected answer. Within a group, values for the same field
	 * are OR'd; separate groups are AND'd by Depict. Net result: OR within an
	 * answer's filters, AND across selected answers (including multi-select
	 * within a single step). The size step is an exception — it contributes a
	 * single OR group, since a shoe matches if available in any of the picked
	 * sizes.
	 */
	groups: Array<Record<string, string[]>>;
	/**
	 * Listing IDs from the LAST step that contributed categories. Replaced (not
	 * accumulated) per step so a later step can fully redirect which listing(s)
	 * are queried.
	 */
	categories?: string[];
};

/**
 * Build the filter payload from selections across currently-visible steps.
 *
 * Each selected answer becomes its own group so multiple picks AND together
 * (e.g. picking both "Barefoot" and "Easy on/off" returns shoes that have
 * BOTH features, not either). Multiple filter values on a single answer are
 * OR'd within that answer's group.
 *
 * The size step is special-cased to one group keyed by `items.name`, OR'ing
 * the picked sizes — a shoe can only have one size value per item, so AND'ing
 * sizes would always return zero.
 */
export function buildFiltersFromSelections(
	visibleSteps: BlokShoeFinderStep[],
	selections: Map<string, Set<string>>,
	upToStepIndex: number,
): ShoeFinderFilterPayload {
	const groups: Array<Record<string, string[]>> = [];
	let latestCategories: string[] | undefined;

	for (let i = 0; i < upToStepIndex; i++) {
		const step = visibleSteps[i];
		if (!step) continue;
		const selectedUids = selections.get(step._uid);
		if (!selectedUids?.size) continue;

		if (step.isLastStep) {
			groups.push({ "items.name": [...selectedUids] });
			continue;
		}

		if (!step.answers) continue;

		const stepCategories: string[] = [];
		for (const answer of step.answers) {
			if (!selectedUids.has(answer._uid)) continue;
			for (const cat of answer.categories ?? []) {
				if (!stepCategories.includes(cat)) stepCategories.push(cat);
			}
			// Depict filters are encoded as "field$$__$$value". One group per
			// answer: values within an answer OR (same-field via op:"in"),
			// different answers AND (separate entries in Depict).
			const answerGroup: Record<string, string[]> = {};
			for (const encoded of answer.filters ?? []) {
				const [field, value] = encoded.split(FILTER_SPLIT_KEY);
				if (!field || !value) continue;
				if (!answerGroup[field]) answerGroup[field] = [];
				if (!answerGroup[field].includes(value)) answerGroup[field].push(value);
			}
			if (Object.keys(answerGroup).length > 0) groups.push(answerGroup);
		}

		if (stepCategories.length > 0) latestCategories = stepCategories;
	}

	return latestCategories ? { groups, categories: latestCategories } : { groups };
}

/**
 * Build a human-readable summary of the user's selections up to (but not
 * including) `upToIndex`. Defaults to all steps when omitted.
 *
 * Size step values are raw size strings; all other steps map selected answer
 * _uids back to their labels.
 */
export function buildSelectionSummary(
	visibleSteps: BlokShoeFinderStep[],
	selections: Map<string, Set<string>>,
	upToIndex = visibleSteps.length,
	sizeLabel = "Size",
): string {
	const parts: string[] = [];

	for (let i = 0; i < upToIndex; i++) {
		const step = visibleSteps[i];
		if (!step) continue;
		const selected = selections.get(step._uid);
		if (!selected || selected.size === 0) continue;

		if (step.isLastStep) {
			const sizes = [...selected];
			parts.push(`${sizeLabel} ${sizes.join(", ")}`);
		} else {
			const labels = step.answers?.filter((a) => selected.has(a._uid)).map((a) => a.label) ?? [];
			parts.push(...labels);
		}
	}

	return parts.join(", ");
}
